import { onMounted, onBeforeMount, onUnmounted, ref } from 'vue'
import onScan from 'onscan.js/onscan.min'
import { useSound, SOUND_TYPE } from '@/Composables/sound'

window._simulate_scan = (code) => onScan.simulate(document, code)

const DELIMITER = '-'

export const SCAN_PREFIX = { ORDER: `order${DELIMITER}` }
export const SCAN_TYPES = { SINGLE: 0, DOUBLE: 1 }

const TWO_SECONDS = 2000
const TEN_SECONDS = 10000

const subscribers = ref(0)

const scanFns = new Map([
  [SCAN_TYPES.SINGLE, new Set()],
  [SCAN_TYPES.DOUBLE, new Set()]
])

let lastScanTimestamp = null
let lastScanIdentifier = null

const prefixCode = (identifier) => {
  return Object.values(SCAN_PREFIX).find((prefix) => identifier.startsWith(prefix))
}

const playSound = ref(null)

const handleScan = (identifier) => {
  try {
    if (playSound.value) {
      playSound.value(SOUND_TYPE.SCAN)
    }

    if (buffer.enabled) {
      buffer.add(identifier)
      return
    }

    const prefix = prefixCode(identifier)
    const scanIdentifier = prefix ? identifier.replace(prefix, '') : identifier

    scanFns.get(SCAN_TYPES.SINGLE).forEach((fn) => {
      fn(scanIdentifier, prefix)
    })

    if (lastScanIdentifier === identifier && Date.now() - lastScanTimestamp < TWO_SECONDS) {
      scanFns.get(SCAN_TYPES.DOUBLE).forEach((fn) => fn(scanIdentifier, prefix))
    }

    lastScanTimestamp = Date.now()
    lastScanIdentifier = identifier
  } catch (error) {
    console.error('Unexpected error in handleScan:', error)
  }
}

const buffer = {
  MAX_ITEMS: 10,
  MAX_AGE: TEN_SECONDS,

  enabled: false,
  items: [],

  start: () => {
    buffer.enabled = true
    buffer.items = []
  },

  add: (identifier) => {
    if (buffer.items.length === buffer.MAX_ITEMS) {
      buffer.items.shift()
    }
    buffer.items.push({ identifier, timestamp: Date.now() })
  },

  flush: (fn) => {
    buffer.enabled = false
    buffer.items
      .filter((item) => Date.now() - item.timestamp < buffer.MAX_AGE)
      .forEach((item) => {
        fn(item.identifier)
      })
    buffer.items = []
  }
}

const keyCodeMapper = (oEvent) => {
  if (oEvent.which == '189') {
    return '-'
  }
  return onScan.decodeKeyEvent(oEvent)
}

export const useScanner = () => {
  const sound = useSound([SOUND_TYPE.SCAN], 'useScanner')
  playSound.value = sound.playSound

  onBeforeMount(() => {
    subscribers.value++
  })

  onMounted(() => {
    if (onScan.isAttachedTo(document)) {
      return
    }
    onScan.attachTo(document, {
      onScan: handleScan,
      keyCodeMapper
    })
  })

  onUnmounted(() => {
    subscribers.value--
    if (subscribers.value === 0) {
      onScan.detachFrom(document)
    }
  })

  const addListener = (fn, { type = SCAN_TYPES.SINGLE } = {}) => {
    if (!fn || typeof fn !== 'function') {
      console.error('fn is required and must be a function')
      return
    }
    if (!scanFns.get(type)) {
      console.error('Unsupported scan type:', type)
      return
    }
    scanFns.get(type).add(fn)
    onUnmounted(() => scanFns.get(type).delete(fn))
  }

  return {
    SCAN_TYPES,
    addListener,
    buffer: {
      start: () => buffer.start(),
      flush: () => buffer.flush(handleScan)
    }
  }
}
